import React, { useMemo } from 'react';
import { useAuth } from '@/contexts/useAuth';

interface PermissionGateProps {
  children: React.ReactNode;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  roles?: string[];
  roleAccess?: any; // Accept roleAccess from parent (for impersonation support)
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permissions = [],
  requireAll = false,
  fallback = null,
  roles = [],
  roleAccess
}) => {
  // Fallback to database roles if roleAccess not provided
  const { user } = useAuth();
  const defaultRoleAccess = useMemo(() => {
    const userRoles = user?.roles || [];

    // Admin and manager have all permissions (superuser check)
    const isAdmin = userRoles.includes('admin') || userRoles.includes('manager');

    return {
      hasPermission: (perm: string) => {
        // Admins and managers have all permissions
        if (isAdmin) {
return true;
}
        // Fallback: check if user has the role (for backward compatibility)
        return userRoles.includes(perm);
      },
      hasAnyPermission: (perms: string[]) => {
        // Admins and managers have all permissions
        if (isAdmin) {
return true;
}
        // Fallback: check if any permission matches a role
        return perms.some((p: string) => userRoles.includes(p));
      },
      hasAllPermissions: (perms: string[]) => {
        // Admins and managers have all permissions
        if (isAdmin) {
return true;
}
        // Fallback: check if all permissions match roles
        return perms.every((p: string) => userRoles.includes(p));
      },
      currentRole: {
        id: userRoles[0] || 'none',
        permissions: userRoles
      }
    };
  }, [user]);

  const effectiveRoleAccess = roleAccess || defaultRoleAccess;

  const { hasAnyPermission, hasAllPermissions, currentRole } = effectiveRoleAccess;

  // Check role-based access
  const hasRoleAccess = roles.length === 0 || roles.includes(currentRole.id);

  // Check permission-based access
  let hasPermissionAccess = true;
  if (permissions.length > 0) {
    hasPermissionAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  const hasAccess = hasRoleAccess && hasPermissionAccess;

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};